import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Switch } from "@/components/ui/switch"
import { TierBadge } from "@/components/apex/TierBadge"
import { ApexPoints } from "@/components/apex/ApexPoints"
import { WishlistManager } from "@/components/apex/WishlistManager"
import { TierProgressionModal } from "@/components/apex/TierProgressionModal"
import { OrderHistory } from "@/components/apex/OrderHistory"
import { AnalyticsDashboard } from "@/components/apex/AnalyticsDashboard"
import { AnimatedCard } from "@/components/apex/AnimatedCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Settings, 
  MessageSquare, 
  Gift, 
  Crown, 
  Star,
  Bell,
  Shield,
  CreditCard,
  HelpCircle,
  LogOut,
  ChevronRight,
  Heart,
  Package,
  TrendingUp,
  BarChart3
} from "lucide-react"

const userProfile = {
  name: "Alex Chen",
  email: "alex.chen@email.com",
  tier: "creator" as const,
  points: 2840,
  nextTierPoints: 5000,
  nextTierName: "Architect",
  joinDate: "March 2024",
  avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
}

const tierBenefits = {
  current: [
    "Silver tier status",
    "Monthly exclusive content", 
    "Style challenge access",
    "Community features",
    "Standard support"
  ],
  next: [
    "Gold tier status",
    "Guaranteed limited drops access",
    "Biannual exclusive gifts",
    "Early collection access", 
    "Dedicated concierge chat",
    "Exclusive events invites"
  ]
}

const achievements = [
  { icon: "🎯", title: "Style Master", description: "Completed 5 style challenges", date: "Last week" },
  { icon: "🔥", title: "Movement Streak", description: "7-day movement streak", date: "Active" },
  { icon: "👑", title: "Creator Tier", description: "Reached Creator status", date: "2 months ago" },
  { icon: "🌟", title: "Community Star", description: "10+ featured posts", date: "Last month" }
]

const settingsOptions = [
  {
    section: "Notifications",
    items: [
      { id: "drops", label: "New drops & releases", enabled: true },
      { id: "challenges", label: "Style challenges", enabled: true },
      { id: "events", label: "Event invites", enabled: false },
      { id: "community", label: "Community activity", enabled: true },
    ]
  },
  {
    section: "Privacy",
    items: [
      { id: "profile", label: "Public profile", enabled: true },
      { id: "activity", label: "Show movement activity", enabled: false },
      { id: "purchases", label: "Show purchase history", enabled: false },
    ]
  }
]

export function ApexProfile() {
  const { state } = useApexState()
  const [settingsEnabled, setSettingsEnabled] = useState<Record<string, boolean>>({
    drops: true,
    challenges: true,
    events: false,
    community: true,
    profile: true,
    activity: false,
    purchases: false
  })

  // Modal states
  const [showWishlist, setShowWishlist] = useState(false)
  const [showTierProgression, setShowTierProgression] = useState(false)
  const [showOrderHistory, setShowOrderHistory] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const toggleSetting = (id: string) => {
    setSettingsEnabled(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  // Use state.user if available, fallback to userProfile
  const user = state.user || userProfile

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground">Profile</h1>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAnalytics(true)}
                className="p-2"
              >
                <BarChart3 size={20} />
              </Button>
              <Button variant="ghost" size="sm" className="p-2">
                <Settings size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Profile Card */}
        <AnimatedCard hoverScale>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="text-lg">{user.name.slice(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground">{user.name}</h2>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <TierBadge tier={user.tier} size="sm" />
                    <span className="text-xs text-muted-foreground">
                      Since {user.joinDate}
                    </span>
                  </div>
                </div>
              </div>

              <ApexPoints
                currentPoints={user.points}
                nextTierPoints={user.tier === 'initiate' ? 1000 : user.tier === 'creator' ? 5000 : 5000}
                tierName={user.tier === 'initiate' ? 'Creator' : user.tier === 'creator' ? 'Architect' : 'Architect'}
              />

              {/* Quick Actions */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowTierProgression(true)}
                  className="flex flex-col gap-1 h-auto py-3"
                >
                  <TrendingUp size={16} />
                  <span className="text-xs">Progress</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowWishlist(true)}
                  className="flex flex-col gap-1 h-auto py-3"
                >
                  <Heart size={16} />
                  <span className="text-xs">Wishlist</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowOrderHistory(true)}
                  className="flex flex-col gap-1 h-auto py-3"
                >
                  <Package size={16} />
                  <span className="text-xs">Orders</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAnalytics(true)}
                  className="flex flex-col gap-1 h-auto py-3"
                >
                  <BarChart3 size={16} />
                  <span className="text-xs">Analytics</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {user.tier === 'creator' ? '12' : user.tier === 'architect' ? '28' : '5'}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Purchases
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-accent mb-1">
                  {user.tier === 'creator' ? '45' : user.tier === 'architect' ? '78' : '23'}
                </div>
                <div className="text-sm text-muted-foreground">
                  Movement Sessions
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>
        </div>

        {/* Tier Benefits */}
        <AnimatedCard>
          <Card className="bg-card-gradient border-0">
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Crown className="text-primary" size={20} />
                Tier Benefits
              </h3>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium text-foreground mb-2">Current Benefits (Creator)</h4>
                <div className="space-y-2">
                  {tierBenefits.current.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-tier-creator"></div>
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <h4 className="font-medium text-foreground mb-2">
                  Unlock with Architect ({user.tier === 'creator' ? (5000 - user.points) : 0} points to go)
                </h4>
                <div className="space-y-2">
                  {tierBenefits.next.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-tier-architect"></div>
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full mt-4 bg-primary hover:bg-primary/90 text-black"
                onClick={() => setShowTierProgression(true)}
              >
                <Crown size={16} className="mr-2" />
                View Full Tier System
              </Button>
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Concierge Access */}
        <AnimatedCard>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageSquare size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Style Concierge</h4>
                    <p className="text-sm text-muted-foreground">
                      Get personalized styling advice
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  {user.tier === 'architect' ? 'Chat Now' : 'Upgrade to Access'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Achievements */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Star className="text-accent" size={20} />
            Achievements
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {achievements.map((achievement, index) => (
              <AnimatedCard key={index} hoverScale>
                <Card className="bg-card-gradient border-0">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl mb-2">{achievement.icon}</div>
                    <h4 className="font-medium text-foreground text-sm mb-1">
                      {achievement.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      {achievement.description}
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      {achievement.date}
                    </Badge>
                  </CardContent>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        </div>

        {/* Settings */}
        <AnimatedCard>
          <Card className="bg-card-gradient border-0">
            <CardHeader>
              <h3 className="text-lg font-semibold">Settings</h3>
            </CardHeader>
            <CardContent className="space-y-6">
              {settingsOptions.map(section => (
                <div key={section.section}>
                  <h4 className="font-medium text-foreground mb-3">{section.section}</h4>
                  <div className="space-y-3">
                    {section.items.map(item => (
                      <div key={item.id} className="flex items-center justify-between">
                        <label className="text-sm text-foreground cursor-pointer">
                          {item.label}
                        </label>
                        <Switch
                          checked={settingsEnabled[item.id]}
                          onCheckedChange={() => toggleSetting(item.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </AnimatedCard>

        {/* Account Actions */}
        <div className="space-y-3">
          <Button 
            variant="outline" 
            className="w-full justify-between"
            onClick={() => setShowAnalytics(true)}
          >
            <div className="flex items-center gap-2">
              <BarChart3 size={16} />
              <span>Activity Analytics</span>
            </div>
            <ChevronRight size={16} />
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-between"
            onClick={() => {}}
          >
            <div className="flex items-center gap-2">
              <CreditCard size={16} />
              <span>Payment Methods</span>
            </div>
            <ChevronRight size={16} />
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-between"
            onClick={() => {}}
          >
            <div className="flex items-center gap-2">
              <Shield size={16} />
              <span>Privacy & Security</span>
            </div>
            <ChevronRight size={16} />
          </Button>

          <Button 
            variant="outline" 
            className="w-full justify-between"
            onClick={() => {}}
          >
            <div className="flex items-center gap-2">
              <HelpCircle size={16} />
              <span>Help & Support</span>
            </div>
            <ChevronRight size={16} />
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-between text-destructive hover:text-destructive"
            onClick={() => {}}
          >
            <div className="flex items-center gap-2">
              <LogOut size={16} />
              <span>Sign Out</span>
            </div>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Modals */}
      <WishlistManager 
        isOpen={showWishlist}
        onClose={() => setShowWishlist(false)}
      />

      <TierProgressionModal
        isOpen={showTierProgression}
        onClose={() => setShowTierProgression(false)}
      />

      <OrderHistory
        isOpen={showOrderHistory}
        onClose={() => setShowOrderHistory(false)}
      />

      <AnalyticsDashboard
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
      />
    </div>
  )
}